import React, { useMemo, useState } from 'react';
import { BsLayers, BsSearch } from 'react-icons/bs';
import { fmtNum } from './normalize';
import UnitRow from './UnitRow';
import { INK } from 'Utils/styleTokens';

const MAX_UNITS = 200;

const card = {
  borderRadius: 12, border: `1px solid ${INK[150]}`, background: '#fff',
  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
};

/** Does a unit match the search term by its name, any algorithm, or any field name? */
function unitMatches(unit, term) {
  if (!term) return true;
  if ((unit.name || '').toLowerCase().includes(term)) return true;
  return (unit.fields || []).some(
    (f) =>
      (f.field || '').toLowerCase().includes(term) ||
      (f.algorithm || '').toLowerCase().includes(term)
  );
}

/**
 * "Table Details" — searchable, capped list of the per-unit (table/file) masking breakdown.
 * Units with errors are pinned to the top; search spans unit name, algorithm, and field name
 * since a job may have tens of thousands of tables.
 */
function TableDetails({ units, isFile, isNoSql, verb = 'masked' }) {
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = (units || []).filter((u) => unitMatches(u, term));
    // Error units first, then the rest by name.
    return list.sort((a, b) => {
      const aErr = a.level === 'failed' ? 0 : 1;
      const bErr = b.level === 'failed' ? 0 : 1;
      if (aErr !== bErr) return aErr - bErr;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [units, q]);

  const shown = filtered.slice(0, MAX_UNITS);
  const noun = isFile ? 'file' : isNoSql ? 'collection' : 'table';

  return (
    <div style={card}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, padding: '14px 20px', borderBottom: `1px solid ${INK[100]}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, color: INK[700] }}>
          <BsLayers size={16} color={INK[400]} />
          Table Details
          <span style={{ marginLeft: 4, fontSize: 14, fontWeight: 400, color: INK[400] }}>({units.length})</span>
        </div>
        <div style={{ position: 'relative', flex: 1, minWidth: 200, marginLeft: 'auto', maxWidth: 360 }}>
          <BsSearch size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: INK[400] }} />
          <input
            data-testid='search-mask-results-tables'
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${noun}, algorithm, or field…`}
            style={{
              width: '100%', borderRadius: 8, border: `1px solid ${INK[200]}`, background: '#fff',
              padding: '6px 12px 6px 30px', fontSize: 14, outline: 'none',
            }}
          />
        </div>
      </div>

      {units.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', fontSize: 14, color: INK[400] }}>
          No {noun}s were {verb} in this job.
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', fontSize: 14, color: INK[400] }}>
          No {noun}s match “{q}”.
        </div>
      ) : (
        <>
          {shown.map((u) => <UnitRow key={u.name} unit={u} isFile={isFile} isNoSql={isNoSql} verb={verb} />)}
          {filtered.length > MAX_UNITS && (
            <div style={{ padding: '12px 20px', textAlign: 'center', fontSize: 12, color: INK[500], borderTop: `1px solid ${INK[100]}` }}>
              Showing {fmtNum(MAX_UNITS)} of {fmtNum(filtered.length)} {noun}s — search to narrow
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default TableDetails;
